import * as Sentry from '@sentry/nextjs'
import Head from 'next/head'
import Router from 'next/router'
import React, { Suspense } from 'react'
import { Provider } from 'urql'

import IconAttention from '@/assets/icons/ic_logic-g-app_attention.svg'
import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import {
  isGuardianTokenError,
  isPartnerTokenError,
  resetUrqlClient,
  useUrqlClient,
} from '@/school-urql'

import { ContractSignErrorBoundary } from './web/school/ContractSignErrorBoundary'

interface Props {
  children: React.ReactNode
}

export function SchoolLayout(props: Props) {
  const client = useUrqlClient()

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Head>

      <Sentry.ErrorBoundary fallback={GenericError}>
        <Suspense
          fallback={
            <div className="pointer-events-none fixed inset-0 grid place-items-center">
              <Loading />
            </div>
          }
        >
          <Provider value={client}>
            <GuardianTokenErrorBoundary>
              <ContractSignErrorBoundary>{props.children}</ContractSignErrorBoundary>
            </GuardianTokenErrorBoundary>
          </Provider>
        </Suspense>
      </Sentry.ErrorBoundary>
    </>
  )
}

export function getSchoolLayout(page: React.ReactNode) {
  return <SchoolLayout>{page}</SchoolLayout>
}

class GuardianTokenErrorBoundary extends React.Component<
  { children?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: {}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: unknown) {
    if (isGuardianTokenError(error) || isPartnerTokenError(error)) {
      return { hasError: true }
    }
  }

  componentDidCatch(error: unknown, _: unknown) {
    if (isGuardianTokenError(error)) {
      resetUrqlClient()
      Router.push('/web/user-setting').then(() => this.setState({ hasError: false }))
    } else if (isPartnerTokenError(error)) {
      resetUrqlClient()
      Router.push('/web/partner-admin').then(() => this.setState({ hasError: false }))
    } else {
      throw error
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="grid h-full place-items-center">
          <Loading />
        </div>
      )
    }

    return this.props.children
  }
}

export const GenericError = (): JSX.Element => {
  return (
    <div className="flex h-[100vh] flex-col items-center bg-white pt-[150px]">
      <h3 className="mb-[15px] text-2xl font-bold leading-[150%] tracking-widest text-primary-text">
        エラーが発生しました
      </h3>

      <p className="text-[15px] leading-[150%] tracking-widest text-secondary-text">
        ホームに戻って、再度お試しください。
      </p>

      <IconAttention width={104} className="mx-auto mt-[40px]" />

      <Button
        asChild
        variant="default"
        className="mt-[100px] h-[47px] w-[268px] rounded-md text-[17px] shadow-medium"
      >
        {
          // eslint-disable-next-line @next/next/no-html-link-for-pages
          <a href="/web/school-admin/">ホームに戻る</a>
        }
      </Button>
    </div>
  )
}
